import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Message } from 'mage-i18n';
import {
  IconInformation,
  Link,
  ParagraphTertiary,
  SpacerVertical,
  SwitchCheckbox,
  Typography,
} from '@klarna/bubble-ui';
import { Alert, ProductHead, ProductPricing } from 'moet-core';
import { Row, Column } from 'mage-components';

import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CY_APPLICATION_CONFIRMATION_CONTAINER_IDS } from '@/containers/ApplicationConfirmation/constants';
import { ANALYTICS_EVENTS } from '@/containers/ApplicationConfirmation/analyticsEvents';
import { logEvent } from '@/utils/analytics';
import { getMerchantCountryKey, getMerchantAwareTranslator } from '@/selectors/merchant';

import SummarySection from './SummarySection';
import styled from 'styled-components';

const tPrefix = 'products-fe.components.product_prices_card';
const tPrefixProducts = 'moet-core.products';

const styles = {
  maxWidth: {
    maxWidth: '100%',
  },
};

const AgreementWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1em;
`;

const ProductPricesCard = ({
  error = '',
  merchantCountryKey,
  isPricingAnnexAccepted,
  isTermsAccepted,
  onChangePricingAnnexAcceptance,
  onChangeTermsAcceptance,
  onOpenPricing,
  onEdit = null,
  pricingAnnex,
  product,
  region,
  t,
  title = '',
}) => {
  return (
    <SummarySection
      title={title}
      onEdit={onEdit}
      titleLink={region !== 'us' ? t(`products-fe.general.pricing_link_${region}`) : undefined}
      onTitleLinkClick={onOpenPricing}
    >
      {product.list.map((price, index) => (
        <React.Fragment key={`product-prices-card-${price.id}`}>
          <Row alignItems="center" justifyContent="center">
            <Column>
              <ProductHead
                productId={product.id}
                productName={t(`${tPrefixProducts}.${price.name}`, merchantCountryKey)}
              />
            </Column>
          </Row>
          <SpacerVertical small />
          <Row additionalStyles={styles.maxWidth}>
            <Column>
              <Typography
                textToken="text-style/text/super-paragraphs/body/regular"
                colorToken="colors/text/neutral"
              >
                {t(`${tPrefixProducts}.${price.description}`, merchantCountryKey)}
              </Typography>
            </Column>
          </Row>
          <SpacerVertical small />
          <Row>
            <Column>
              {price.price ? (
                <ProductPricing price={price.price} pricingAnnex={pricingAnnex} />
              ) : (
                <Typography
                  textToken="text-style/text/super-paragraphs/body/medium"
                  colorToken="colors/text/neutral"
                >
                  -*
                </Typography>
              )}
            </Column>
          </Row>
          {index !== product.list.length - 1 && <SpacerVertical medium />}
        </React.Fragment>
      ))}
      {!product.homeMarketPriceAvailable && (
        <>
          <SpacerVertical medium />
          <Row additionalStyles={styles.maxWidth}>
            <Column>
              <ParagraphTertiary light correctFootprint>
                <Message path={`${tPrefix}.product_not_available_in_your_country`} />
              </ParagraphTertiary>
            </Column>
          </Row>
        </>
      )}
      <SpacerVertical medium />
      <AgreementWrapper>
        <SwitchCheckbox
          onChange={onChangeTermsAcceptance}
          checked={isTermsAccepted}
          {...generateCyElemId(
            CY_APPLICATION_CONFIRMATION_CONTAINER_IDS.CHECKBOX_TERMS_AND_CONDITIONS,
            window,
          )}
        />
        <Typography textToken="text-style/text/super-paragraphs/body/regular">
          <Message path={`${tPrefix}${pricingAnnex ? '.merchant_agreement' : ''}.before_link`} />
          &nbsp;
          <Link
            isUnderlined
            href={product.termsAndConditionsLink}
            target="_blank"
            onPress={() => logEvent(ANALYTICS_EVENTS.openMerchantAgreement)}
          >
            <Message path={`${tPrefix}${pricingAnnex ? '.merchant_agreement' : ''}.link_text`} />
          </Link>
          &nbsp;
          {product.list.length > 1 ? (
            <Message
              path={`${tPrefix}${
                pricingAnnex ? '.merchant_agreement' : ''
              }.after_link_more_than_one_product`}
            />
          ) : (
            <Message
              path={`${tPrefix}${pricingAnnex ? '.merchant_agreement' : ''}.after_link_one_product`}
            />
          )}
        </Typography>
      </AgreementWrapper>

      {pricingAnnex && (
        <AgreementWrapper>
          <SwitchCheckbox
            onChange={onChangePricingAnnexAcceptance}
            checked={isPricingAnnexAccepted}
            {...generateCyElemId(
              CY_APPLICATION_CONFIRMATION_CONTAINER_IDS.CHECKBOX_PRICING_ANNEX,
              window,
            )}
          />
          <Typography textToken="text-style/text/super-paragraphs/body/regular">
            <Message path={`${tPrefix}.pricing_annex.before_link`} />
            &nbsp;
            <Link
              isUnderlined
              href={pricingAnnex}
              target="_blank"
              onPress={() => logEvent(ANALYTICS_EVENTS.openPricingTerms)}
            >
              <Message path={`${tPrefix}.pricing_annex.link_text`} />
            </Link>
            &nbsp;
            <Message path={`${tPrefix}.pricing_annex.after_link_one_product`} />
          </Typography>
        </AgreementWrapper>
      )}
      {error && <Alert type="error" content={error} icon={IconInformation} />}
    </SummarySection>
  );
};

ProductPricesCard.propTypes = {
  countryLabel: PropTypes.string.isRequired,
  isPricingAnnexAccepted: PropTypes.bool.isRequired,
  isTermsAccepted: PropTypes.bool.isRequired,
  merchantCountryKey: PropTypes.string.isRequired,
  region: PropTypes.oneOf(['eu', 'us']).isRequired,
  t: PropTypes.func.isRequired,
  onChangePricingAnnexAcceptance: PropTypes.func.isRequired,
  onChangeTermsAcceptance: PropTypes.func.isRequired,
  onOpenPricing: PropTypes.func.isRequired,
  error: PropTypes.string,
  pricingAnnex: PropTypes.string,
  product: PropTypes.shape({
    homeMarketPriceAvailable: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    termsAndConditionsLink: PropTypes.string.isRequired,
    list: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        price: PropTypes.object,
      }),
    ),
  }),
  title: PropTypes.string,
  onEdit: PropTypes.func,
};

export default connect(state => ({
  merchantCountryKey: getMerchantCountryKey(state),
  t: getMerchantAwareTranslator(state),
}))(ProductPricesCard);
